import { useState, useEffect, useCallback, useRef } from "react";
import { 
  FileText, Upload, Search, X, Check, AlertTriangle, Clock, 
  Euro, Calendar, Building, User, Hash, CreditCard, Filter,
  Eye, Link2, Trash2, Download, ChevronDown, RefreshCw, ArrowDownLeft, Edit2
} from "lucide-react";
import getApiUrl from './utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Statuts de paiement
const STATUTS = {
  a_payer: { label: "À payer", color: "bg-orange-500", icon: Clock },
  paye: { label: "Payé", color: "bg-green-500", icon: Check },
  retard: { label: "En retard", color: "bg-red-500", icon: AlertTriangle },
  litige: { label: "Litige", color: "bg-purple-500", icon: AlertTriangle },
  partiel: { label: "Partiel", color: "bg-blue-500", icon: Euro }
};

// Formater un montant (négatif pour les avoirs si besoin)
function formatMontant(val, isAvoir = false) {
  if (!val) return "-";
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  const displayNum = isAvoir ? -Math.abs(num) : num;
  return displayNum.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

// Formater une date
function formatDate(val) {
  if (!val) return "-";
  return val;
}

export default function FacturesPage({ onBack, embedded = false }) {
  const [factures, setFactures] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [filter, setFilter] = useState("toutes"); // toutes, a_rattacher, rattachees
  const [statutFilter, setStatutFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [editingFacture, setEditingFacture] = useState(null); // Pour l'édition d'une facture
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [showRattacher, setShowRattacher] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null); // Pour la confirmation avant ajout
  const [showRegler, setShowRegler] = useState(null); // Pour régler une facture
  const [avoirsDisponibles, setAvoirsDisponibles] = useState([]);
  const fileInputRef = useRef(null);

  // Ouvrir le formulaire d'édition
  const openEditForm = (facture) => {
    setEditForm({
      fournisseur: facture.fournisseur || '',
      numero_facture: facture.numero_facture || '',
      date_facture: facture.date_facture || '',
      date_echeance: facture.date_echeance || '',
      reference_commande: facture.reference_commande || '',
      montant_ht: facture.montant_ht || '',
      montant_tva: facture.montant_tva || '',
      montant_ttc: facture.montant_ttc || '',
      taux_tva: facture.taux_tva || 20,
      type_document: facture.type_document || 'FACTURE',
      description: facture.description || '',
      conditions_paiement: facture.conditions_paiement || ''
    });
    setEditingFacture(facture);
  };

  // Sauvegarder les modifications
  const saveEdit = async () => {
    if (!editingFacture) return;
    setSavingEdit(true);
    try {
      const res = await authFetch(`${API}/api/factures/${editingFacture._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      const data = await res.json();
      if (data.success) {
        loadFactures();
        loadStats();
        setEditingFacture(null);
        setSelectedFacture(null);
      } else {
        alert("Erreur lors de la sauvegarde: " + (data.error || "Erreur inconnue"));
      }
    } catch (e) {
      console.error("Erreur édition:", e);
      alert("Erreur lors de la sauvegarde");
    }
    setSavingEdit(false);
  };

  // Charger les factures
  const loadFactures = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/factures?`;
      if (filter === "a_rattacher") url += "non_rattachees=true&";
      if (statutFilter) url += `statut=${statutFilter}&`;
      
      const res = await authFetch(url);
      const data = await res.json();
      setFactures(data.factures || []);
    } catch (e) {
      console.error("Erreur chargement factures:", e);
    }
    setLoading(false);
  }, [filter, statutFilter]);

  // Charger les stats
  const loadStats = async () => {
    try {
      const res = await authFetch(`${API}/api/factures/stats`);
      const data = await res.json();
      setStats(data);
    } catch (e) {
      console.error("Erreur chargement stats:", e);
    }
  };

  useEffect(() => { loadFactures(); loadStats(); }, [loadFactures]);

  // Upload de fichier (étape 1 - extraction et preview)
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setDuplicateWarning(null);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length, filename: file.name });
      
      const formData = new FormData();
      formData.append("file", file);
      
      try {
        const res = await authFetch(`${API}/api/factures/upload`, {
          method: "POST",
          body: formData
        });
        const data = await res.json();
        
        if (data.success && data.needs_confirmation) {
          // Ouvrir le modal de confirmation avec les données extraites
          setPendingConfirm({
            extracted_data: data.extracted_data,
            file_info: data.file_info,
            avoirs_disponibles: data.avoirs_disponibles || [],
            extraction_success: data.extraction_success
          });
        } else if (data.error === "doublon" || data.error === "doublon_numero") {
          setDuplicateWarning({
            type: "doublon",
            message: data.message,
            existing: data.existing_facture
          });
        }
      } catch (e) {
        console.error("Erreur upload:", e);
      }
    }
    
    setUploading(false);
    setUploadProgress(null);
  };

  // Confirmer l'ajout de facture (étape 2)
  const confirmFacture = async (modifications = {}) => {
    if (!pendingConfirm) return;
    
    try {
      const formData = new FormData();
      formData.append("filename", pendingConfirm.file_info.filename);
      formData.append("file_hash", pendingConfirm.file_info.hash);
      formData.append("file_base64", pendingConfirm.file_info.file_base64);
      formData.append("extracted_data", JSON.stringify(pendingConfirm.extracted_data));
      formData.append("modifications", JSON.stringify(modifications));
      
      const res = await authFetch(`${API}/api/factures/confirm`, {
        method: "POST",
        body: formData
      });
      const data = await res.json();
      
      if (data.success) {
        setPendingConfirm(null);
        loadFactures();
        loadStats();
      }
    } catch (e) {
      console.error("Erreur confirmation:", e);
    }
  };

  // Régler une facture
  const reglerFacture = async (factureId, reglementData) => {
    try {
      const formData = new FormData();
      formData.append("date_reglement", reglementData.date_reglement);
      formData.append("montant_regle", reglementData.montant_regle);
      formData.append("mode_paiement", reglementData.mode_paiement);
      if (reglementData.avoir_id) {
        formData.append("avoir_id", reglementData.avoir_id);
      }
      
      const res = await authFetch(`${API}/api/factures/${factureId}/regler`, {
        method: "POST",
        body: formData
      });
      const data = await res.json();
      
      if (data.success) {
        setShowRegler(null);
        loadFactures();
        loadStats();
      }
    } catch (e) {
      console.error("Erreur règlement:", e);
    }
  };

  // Charger les avoirs disponibles
  const loadAvoirsDisponibles = async () => {
    try {
      const res = await authFetch(`${API}/api/factures/avoirs-disponibles`);
      const data = await res.json();
      setAvoirsDisponibles(data.avoirs || []);
    } catch (e) {
      console.error("Erreur chargement avoirs:", e);
    }
  };

  // Drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Rechercher des clients pour rattachement
  const searchClients = async (q) => {
    setClientsLoading(true);
    try {
      const res = await authFetch(`${API}/api/factures/clients/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setClients(data.clients || []);
    } catch (e) {
      console.error("Erreur recherche clients:", e);
    }
    setClientsLoading(false);
  };

  useEffect(() => {
    if (showRattacher) {
      const timer = setTimeout(() => searchClients(clientSearch), 300);
      return () => clearTimeout(timer);
    }
  }, [clientSearch, showRattacher]);

  // Rattacher une facture
  const rattacherFacture = async (factureId, clientId, clientNom) => {
    try {
      const formData = new FormData();
      formData.append("client_id", clientId);
      formData.append("client_nom", clientNom);
      
      const res = await authFetch(`${API}/api/factures/${factureId}/rattacher`, {
        method: "POST",
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setShowRattacher(null);
        loadFactures();
        loadStats();
      }
    } catch (e) {
      console.error("Erreur rattachement:", e);
    }
  };

  // Détacher une facture d'un client
  const detacherFacture = async (factureId) => {
    if (!window.confirm("Détacher cette facture du client ?")) return;
    try {
      const res = await authFetch(`${API}/api/factures/${factureId}/detacher`, {
        method: "POST"
      });
      const data = await res.json();
      if (data.success) {
        setSelectedFacture(null);
        loadFactures();
        loadStats();
      }
    } catch (e) {
      console.error("Erreur détachement:", e);
    }
  };

  // Changer le statut
  const changerStatut = async (factureId, statut) => {
    try {
      const res = await authFetch(`${API}/api/factures/${factureId}/statut?statut=${statut}`, {
        method: "PUT"
      });
      const data = await res.json();
      if (data.success) {
        loadFactures();
        loadStats();
      }
    } catch (e) {
      console.error("Erreur changement statut:", e);
    }
  };

  // Supprimer une facture
  const supprimerFacture = async (factureId) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    try {
      const res = await authFetch(`${API}/api/factures/${factureId}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        loadFactures();
        loadStats();
        setSelectedFacture(null);
      }
    } catch (e) {
      console.error("Erreur suppression:", e);
    }
  };

  // Filtrer les factures par recherche
  const filteredFactures = factures.filter(f => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (f.fournisseur && f.fournisseur.toLowerCase().includes(q)) ||
      (f.numero_facture && f.numero_facture.toLowerCase().includes(q)) ||
      (f.client_nom && f.client_nom.toLowerCase().includes(q)) ||
      (f.filename && f.filename.toLowerCase().includes(q))
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Factures Chantiers</h1>
              <p className="text-slate-400 text-sm">Gérez vos factures et rattachez-les aux clients</p>
            </div>
          </div>
          <button
            onClick={() => onBack ? onBack() : window.history.back()}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            ← Retour
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total || 0}</p>
                <p className="text-xs text-slate-400">Total factures</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.a_payer || 0}</p>
                <p className="text-xs text-slate-400">À payer</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Check className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.paye || 0}</p>
                <p className="text-xs text-slate-400">Payées</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.retard || 0}</p>
                <p className="text-xs text-slate-400">En retard</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Link2 className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.non_rattachees || 0}</p>
                <p className="text-xs text-slate-400">À rattacher</p>
              </div>
            </div>
          </div>
        </div>

        {/* Zone Upload */}
        <div
          className={`relative mb-6 p-8 rounded-2xl border-2 border-dashed transition-all ${
            uploading ? "border-amber-500 bg-amber-500/10" : "border-slate-600 bg-slate-800/30 hover:border-amber-500/50"
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            multiple
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files)}
          />
          <div className="flex flex-col items-center gap-4">
            {uploading ? (
              <>
                <div className="w-16 h-16 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
                <p className="text-white font-medium">
                  Upload en cours... {uploadProgress?.current}/{uploadProgress?.total}
                </p>
                <p className="text-slate-400 text-sm">{uploadProgress?.filename}</p>
              </>
            ) : (
              <>
                <Upload className="w-12 h-12 text-amber-500" />
                <div className="text-center">
                  <p className="text-white font-medium">Glissez vos factures ici</p>
                  <p className="text-slate-400 text-sm">ou cliquez pour sélectionner (PDF, PNG, JPG)</p>
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-medium rounded-lg hover:from-amber-400 hover:to-orange-500 transition-all"
                >
                  Sélectionner des fichiers
                </button>
              </>
            )}
          </div>
        </div>

        {/* Alerte doublon/suggestion */}
        {duplicateWarning && (
          <div className={`mb-6 p-4 rounded-xl border ${
            duplicateWarning.type === "doublon" 
              ? "bg-red-500/10 border-red-500/30" 
              : "bg-blue-500/10 border-blue-500/30"
          }`}>
            <div className="flex items-start gap-3">
              {duplicateWarning.type === "doublon" ? (
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              ) : (
                <User className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={duplicateWarning.type === "doublon" ? "text-red-200" : "text-blue-200"}>
                  {duplicateWarning.message}
                </p>
                {duplicateWarning.type === "suggestion" && duplicateWarning.client && (
                  <button
                    onClick={() => {
                      rattacherFacture(
                        duplicateWarning.facture._id,
                        duplicateWarning.client.client_id || duplicateWarning.client.row_number,
                        `${duplicateWarning.client.nom} ${duplicateWarning.client.prenom}`
                      );
                      setDuplicateWarning(null);
                    }}
                    className="mt-2 px-4 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-400"
                  >
                    Rattacher à ce client
                  </button>
                )}
              </div>
              <button onClick={() => setDuplicateWarning(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setFilter("toutes")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === "toutes" ? "bg-amber-500 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              Toutes
            </button>
            <button
              onClick={() => setFilter("a_rattacher")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === "a_rattacher" ? "bg-amber-500 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              À rattacher
            </button>
            <button
              onClick={() => setFilter("rattachees")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === "rattachees" ? "bg-amber-500 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              Rattachées
            </button>
          </div>

          <select
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value)}
            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          >
            <option value="">Tous les statuts</option>
            <option value="a_payer">À payer</option>
            <option value="paye">Payé</option>
            <option value="retard">En retard</option>
            <option value="litige">Litige</option>
          </select>

          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher fournisseur, n° facture, client..."
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500"
            />
          </div>

          <button
            onClick={() => { loadFactures(); loadStats(); }}
            className="p-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Liste des factures */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-400">Chargement des factures...</p>
            </div>
          ) : filteredFactures.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Aucune facture trouvée</p>
              <p className="text-slate-500 text-sm mt-1">Uploadez votre première facture ci-dessus</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Type</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Fournisseur</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">N° Facture</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Réf. Cde</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Date Facture</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Échéance</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-slate-400 uppercase">Montant HT</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-slate-400 uppercase">Montant TTC</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Client</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Réglée</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Statut</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {filteredFactures.map((facture) => {
                    const statut = STATUTS[facture.statut_paiement] || STATUTS.a_payer;
                    const StatutIcon = statut.icon;
                    const isAvoir = facture.type_document?.toUpperCase() === 'AVOIR';
                    const isAcompte = facture.type_document?.toUpperCase() === "FACTURE D'ACOMPTE";
                    
                    return (
                      <tr key={facture._id} className={`hover:bg-slate-700/30 transition-colors ${isAvoir ? 'bg-pink-500/5' : isAcompte ? 'bg-cyan-500/5' : ''}`}>
                        <td className="px-3 py-3">
                          {isAvoir ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold bg-pink-500/20 text-pink-400 border border-pink-500/30">
                              <ArrowDownLeft className="w-3 h-3" />
                              AVOIR
                            </span>
                          ) : isAcompte ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                              <CreditCard className="w-3 h-3" />
                              ACOMPTE
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-slate-700/50 text-slate-300">
                              <FileText className="w-3 h-3" />
                              Facture
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded flex items-center justify-center ${isAvoir ? 'bg-pink-500/20' : isAcompte ? 'bg-cyan-500/20' : 'bg-slate-700'}`}>
                              <Building className={`w-3.5 h-3.5 ${isAvoir ? 'text-pink-400' : isAcompte ? 'text-cyan-400' : 'text-slate-400'}`} />
                            </div>
                            <span className={`font-medium truncate max-w-[180px] ${isAvoir ? 'text-pink-200' : isAcompte ? 'text-cyan-200' : 'text-white'}`}>
                              {facture.fournisseur || "Non identifié"}
                            </span>
                          </div>
                        </td>
                        <td className={`px-3 py-3 ${isAvoir ? 'text-pink-300' : isAcompte ? 'text-cyan-300' : 'text-slate-300'}`}>{facture.numero_facture || "-"}</td>
                        <td className="px-3 py-3 text-slate-400 text-sm">{facture.reference_commande || "-"}</td>
                        <td className={`px-3 py-3 ${isAvoir ? 'text-pink-300' : isAcompte ? 'text-cyan-300' : 'text-slate-300'}`}>{formatDate(facture.date_facture)}</td>
                        <td className={`px-3 py-3 ${isAvoir ? 'text-pink-300' : isAcompte ? 'text-cyan-300' : 'text-red-400'}`}>{formatDate(facture.date_echeance) || "-"}</td>
                        <td className={`px-3 py-3 text-right font-medium ${isAvoir ? 'text-pink-300' : isAcompte ? 'text-cyan-300' : 'text-blue-400'}`}>
                          {isAvoir ? '-' : ''}{formatMontant(facture.montant_ht)}
                        </td>
                        <td className={`px-3 py-3 text-right font-medium ${isAvoir ? 'text-pink-400' : isAcompte ? 'text-cyan-400' : 'text-white'}`}>
                          {isAvoir ? '-' : ''}{formatMontant(facture.montant_ttc)}
                        </td>
                        <td className="px-3 py-3">
                          {facture.client_nom ? (
                            <div className="flex items-center gap-2">
                              <span className="text-green-400">{facture.client_nom}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); detacherFacture(facture._id); }}
                                className="text-red-400 hover:text-red-300 opacity-60 hover:opacity-100"
                                title="Détacher"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setShowRattacher(facture); setClientSearch(""); searchClients(""); }}
                              className="text-amber-400 hover:text-amber-300 text-sm flex items-center gap-1"
                            >
                              <Link2 className="w-3 h-3" /> Rattacher
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {facture.est_reglee ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 w-fit">
                                <Check className="w-3 h-3" />
                                Réglée
                              </span>
                              {facture.mode_paiement && (
                                <span className="text-xs text-green-300/80 pl-1 truncate max-w-[120px]" title={facture.mode_paiement}>
                                  {facture.mode_paiement}
                                </span>
                              )}
                            </div>
                          ) : !isAvoir ? (
                            <button
                              onClick={() => { setShowRegler(facture); loadAvoirsDisponibles(); }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors border border-orange-500/30"
                            >
                              <Euro className="w-3 h-3" />
                              Régler
                            </button>
                          ) : (
                            <span className="text-slate-500 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="relative group">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium text-white ${statut.color}`}>
                              <StatutIcon className="w-3 h-3" />
                              {statut.label}
                            </span>
                            {/* Dropdown statut au hover */}
                            <div className="absolute top-full left-0 mt-1 hidden group-hover:block z-10">
                              <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-1 min-w-[120px]">
                                {Object.entries(STATUTS).map(([key, val]) => (
                                  <button
                                    key={key}
                                    onClick={() => changerStatut(facture._id, key)}
                                    className="w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-slate-700 rounded flex items-center gap-2"
                                  >
                                    <div className={`w-2 h-2 rounded-full ${val.color}`} />
                                    {val.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setSelectedFacture(facture)}
                              className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                              title="Voir détails"
                            >
                              <Eye className="w-4 h-4 text-slate-400" />
                            </button>
                            <button
                              onClick={() => openEditForm(facture)}
                              className="p-1.5 hover:bg-blue-500/20 rounded transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4 text-blue-400" />
                            </button>
                            {facture.drive_link ? (
                              <a
                                href={facture.drive_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                                title="Voir sur Drive"
                              >
                                <Download className="w-4 h-4 text-slate-400" />
                              </a>
                            ) : facture.gridfs_id ? (
                              <a
                                href={`${API}/api/factures/${facture._id}/file`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                                title="Voir le fichier"
                              >
                                <Download className="w-4 h-4 text-slate-400" />
                              </a>
                            ) : null}
                            <button
                              onClick={() => supprimerFacture(facture._id)}
                              className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Détails Facture */}
      {selectedFacture && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'ring-2 ring-pink-500/50' : ''}`}>
            <div className={`sticky top-0 px-6 py-4 border-b flex items-center justify-between ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'bg-pink-500/10 border-pink-500/30' : 'bg-slate-800 border-slate-700'}`}>
              <div className="flex items-center gap-3">
                {selectedFacture.type_document?.toUpperCase() === 'AVOIR' && (
                  <div className="w-10 h-10 bg-pink-500/30 rounded-lg flex items-center justify-center">
                    <ArrowDownLeft className="w-5 h-5 text-pink-400" />
                  </div>
                )}
                <h2 className={`text-xl font-bold ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'text-pink-400' : 'text-white'}`}>
                  {selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'Détails de l\'avoir' : 'Détails de la facture'}
                </h2>
              </div>
              <button onClick={() => setSelectedFacture(null)} className="p-2 hover:bg-slate-700 rounded-lg">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Badge AVOIR si c'est un avoir */}
              {selectedFacture.type_document?.toUpperCase() === 'AVOIR' && (
                <div className="bg-pink-500/20 rounded-xl p-4 border-2 border-pink-500/50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-pink-500/30 rounded-xl flex items-center justify-center">
                      <ArrowDownLeft className="w-6 h-6 text-pink-400" />
                    </div>
                    <div>
                      <p className="text-pink-400 font-bold text-lg">AVOIR</p>
                      <p className="text-pink-300 text-sm">Ce document est un avoir (crédit/remboursement)</p>
                      {selectedFacture.facture_origine && (
                        <p className="text-pink-200 text-xs mt-1">Facture d'origine : {selectedFacture.facture_origine}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Infos principales */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`rounded-xl p-4 ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'bg-pink-500/10 border border-pink-500/20' : 'bg-slate-700/50'}`}>
                  <p className="text-xs text-slate-400 mb-1">Fournisseur</p>
                  <p className={`font-medium ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'text-pink-200' : 'text-white'}`}>{selectedFacture.fournisseur || "-"}</p>
                </div>
                <div className={`rounded-xl p-4 ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'bg-pink-500/10 border border-pink-500/20' : 'bg-slate-700/50'}`}>
                  <p className="text-xs text-slate-400 mb-1">N° {selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'Avoir' : 'Facture'}</p>
                  <p className={`font-medium ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'text-pink-200' : 'text-white'}`}>{selectedFacture.numero_facture || "-"}</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-xs text-slate-400 mb-1">Date {selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'avoir' : 'facture'}</p>
                  <p className="text-white font-medium">{formatDate(selectedFacture.date_facture)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-xs text-slate-400 mb-1">Date échéance</p>
                  <p className="text-white font-medium">{formatDate(selectedFacture.date_echeance)}</p>
                </div>
              </div>

              {/* Commande */}
              {(selectedFacture.reference_commande || selectedFacture.date_commande) && (
                <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
                  <h3 className="text-blue-400 font-medium mb-3">Commande</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-400">Réf. Commande</p>
                      <p className="text-white font-medium">{selectedFacture.reference_commande || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Date Commande</p>
                      <p className="text-white font-medium">{formatDate(selectedFacture.date_commande)}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Montants */}
              <div className={`rounded-xl p-4 border ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'bg-gradient-to-r from-pink-500/20 to-pink-600/20 border-pink-500/30' : 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30'}`}>
                <h3 className={`font-medium mb-3 ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'text-pink-400' : 'text-amber-400'}`}>
                  Montants {selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? '(Crédit)' : ''}
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-400">HT</p>
                    <p className={`font-bold ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'text-pink-300' : 'text-white'}`}>
                      {selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? '-' : ''}{formatMontant(selectedFacture.montant_ht)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">TVA ({selectedFacture.taux_tva || 20}%)</p>
                    <p className={`font-bold ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'text-pink-300' : 'text-white'}`}>
                      {selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? '-' : ''}{formatMontant(selectedFacture.montant_tva)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">TTC</p>
                    <p className={`font-bold text-lg ${selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? 'text-pink-400' : 'text-white'}`}>
                      {selectedFacture.type_document?.toUpperCase() === 'AVOIR' ? '-' : ''}{formatMontant(selectedFacture.montant_ttc)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Statut Règlement */}
              <div className={`rounded-xl p-4 border ${selectedFacture.est_reglee ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-700/50 border-slate-600'}`}>
                <h3 className={`font-medium mb-3 ${selectedFacture.est_reglee ? 'text-green-400' : 'text-slate-400'}`}>
                  {selectedFacture.est_reglee ? '✓ Document Réglé' : 'Règlement'}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400">Réglée ?</p>
                    <p className={`font-medium ${selectedFacture.est_reglee ? 'text-green-400' : 'text-orange-400'}`}>
                      {selectedFacture.est_reglee ? 'Oui' : 'Non'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Mode / Modalité</p>
                    <p className="text-white font-medium">{selectedFacture.mode_paiement || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Référence règlement</p>
                    <p className="text-white font-medium">{selectedFacture.reference_reglement || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Date règlement</p>
                    <p className="text-white font-medium">{formatDate(selectedFacture.date_reglement)}</p>
                  </div>
                  {selectedFacture.montant_regle && (
                    <div>
                      <p className="text-xs text-slate-400">Montant réglé</p>
                      <p className="text-green-400 font-medium">{formatMontant(selectedFacture.montant_regle)}</p>
                    </div>
                  )}
                  {selectedFacture.reste_a_payer && parseFloat(selectedFacture.reste_a_payer) > 0 && (
                    <div>
                      <p className="text-xs text-slate-400">Reste à payer</p>
                      <p className="text-orange-400 font-medium">{formatMontant(selectedFacture.reste_a_payer)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Autres infos */}
              <div className="space-y-3">
                {selectedFacture.conditions_paiement && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <CreditCard className="w-4 h-4 text-slate-500" />
                    <span>Conditions: {selectedFacture.conditions_paiement}</span>
                  </div>
                )}
                {selectedFacture.type_prestation && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span>Type: {selectedFacture.type_prestation}</span>
                  </div>
                )}
                {selectedFacture.description && (
                  <div className="flex items-start gap-3 text-slate-300">
                    <FileText className="w-4 h-4 text-slate-500 mt-1" />
                    <span>{selectedFacture.description}</span>
                  </div>
                )}
              </div>

              {/* Client rattaché */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <p className="text-xs text-slate-400 mb-2">Client rattaché</p>
                {selectedFacture.client_nom ? (
                  <div className="flex items-center justify-between">
                    <p className="text-green-400 font-medium">{selectedFacture.client_nom}</p>
                    <button
                      onClick={() => detacherFacture(selectedFacture._id)}
                      className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Détacher
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setShowRattacher(selectedFacture); setSelectedFacture(null); }}
                    className="text-amber-400 hover:text-amber-300"
                  >
                    + Rattacher à un client
                  </button>
                )}
              </div>

              {/* Lien vers le fichier */}
              {(selectedFacture.drive_link || selectedFacture.gridfs_id) && (
                <a
                  href={selectedFacture.drive_link || `${API}/api/factures/${selectedFacture._id}/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 bg-blue-500 hover:bg-blue-400 text-white text-center rounded-lg font-medium transition-colors"
                >
                  <Download className="w-4 h-4 inline mr-2" />
                  {selectedFacture.drive_link ? 'Voir le document sur Google Drive' : 'Voir le document'}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Rattacher Client */}
      {showRattacher && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Rattacher à un client</h2>
              <button onClick={() => setShowRattacher(null)} className="p-2 hover:bg-slate-700 rounded-lg">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-slate-400 text-sm mb-4">
                Facture: <span className="text-white">{showRattacher.fournisseur} - {showRattacher.numero_facture}</span>
              </p>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Rechercher un client..."
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500"
                  autoFocus
                />
              </div>
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {clientsLoading ? (
                  <div className="text-center py-4 text-slate-400">Chargement...</div>
                ) : clients.length === 0 ? (
                  <div className="text-center py-4 text-slate-400">Aucun client trouvé</div>
                ) : (
                  clients.map((client) => (
                    <button
                      key={client.client_id || client.row_number}
                      onClick={() => rattacherFacture(showRattacher._id, client.client_id, `${client.nom} ${client.prenom}`)}
                      className="w-full p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-left transition-colors flex items-center justify-between"
                    >
                      <div>
                        <p className="text-white font-medium">{client.nom} {client.prenom}</p>
                        <p className="text-slate-400 text-sm">
                          Dép. {client.dpt}
                          {client.id_qhare && <span className="ml-2 text-blue-400">ID: {client.id_qhare}</span>}
                        </p>
                      </div>
                      <Link2 className="w-4 h-4 text-amber-400" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Ajout Facture */}
      {pendingConfirm && (
        <ConfirmationModal
          data={pendingConfirm}
          onConfirm={confirmFacture}
          onCancel={() => setPendingConfirm(null)}
          formatMontant={formatMontant}
          formatDate={formatDate}
        />
      )}

      {/* Modal Régler Facture */}
      {showRegler && (
        <ReglementModal
          facture={showRegler}
          avoirsDisponibles={avoirsDisponibles}
          onConfirm={(data) => reglerFacture(showRegler._id, data)}
          onCancel={() => setShowRegler(null)}
          formatMontant={formatMontant}
        />
      )}

      {/* Modal Édition Facture */}
      {editingFacture && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ring-2 ring-blue-500/50">
            <div className="sticky top-0 px-6 py-4 border-b bg-blue-500/10 border-blue-500/30 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/30 rounded-lg flex items-center justify-center">
                  <Edit2 className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Modifier la facture</h2>
              </div>
              <button onClick={() => setEditingFacture(null)} className="p-2 hover:bg-slate-700 rounded-lg">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Type document et Fournisseur */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Type de document</label>
                  <select
                    value={editForm.type_document}
                    onChange={(e) => setEditForm(prev => ({ ...prev, type_document: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border bg-slate-700 text-white border-slate-600 focus:outline-none focus:border-blue-500"
                  >
                    <option value="FACTURE">FACTURE</option>
                    <option value="FACTURE D'ACOMPTE">FACTURE D'ACOMPTE</option>
                    <option value="AVOIR">AVOIR</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Fournisseur *</label>
                  <input
                    type="text"
                    value={editForm.fournisseur}
                    onChange={(e) => setEditForm(prev => ({ ...prev, fournisseur: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Numéro facture et Réf commande */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">N° Facture</label>
                  <input
                    type="text"
                    value={editForm.numero_facture}
                    onChange={(e) => setEditForm(prev => ({ ...prev, numero_facture: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Réf. Commande</label>
                  <input
                    type="text"
                    value={editForm.reference_commande}
                    onChange={(e) => setEditForm(prev => ({ ...prev, reference_commande: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Date Facture</label>
                  <input
                    type="text"
                    value={editForm.date_facture}
                    onChange={(e) => setEditForm(prev => ({ ...prev, date_facture: e.target.value }))}
                    placeholder="JJ/MM/AAAA"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Date Échéance</label>
                  <input
                    type="text"
                    value={editForm.date_echeance}
                    onChange={(e) => setEditForm(prev => ({ ...prev, date_echeance: e.target.value }))}
                    placeholder="JJ/MM/AAAA"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Montants */}
              <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
                <h3 className="text-amber-400 font-medium mb-3">Montants</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Montant HT</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.montant_ht}
                      onChange={(e) => {
                        const ht = parseFloat(e.target.value) || 0;
                        const tva = ht * (editForm.taux_tva / 100);
                        setEditForm(prev => ({
                          ...prev,
                          montant_ht: e.target.value,
                          montant_tva: tva.toFixed(2),
                          montant_ttc: (ht + tva).toFixed(2)
                        }));
                      }}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">TVA %</label>
                    <select
                      value={editForm.taux_tva}
                      onChange={(e) => {
                        const taux = parseFloat(e.target.value);
                        const ht = parseFloat(editForm.montant_ht) || 0;
                        const tva = ht * (taux / 100);
                        setEditForm(prev => ({
                          ...prev,
                          taux_tva: taux,
                          montant_tva: tva.toFixed(2),
                          montant_ttc: (ht + tva).toFixed(2)
                        }));
                      }}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                    >
                      <option value="20">20%</option>
                      <option value="10">10%</option>
                      <option value="5.5">5.5%</option>
                      <option value="0">0%</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">TVA €</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.montant_tva}
                      readOnly
                      className="w-full px-3 py-2 bg-slate-600 border border-slate-600 rounded-lg text-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Montant TTC</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.montant_ttc}
                      onChange={(e) => setEditForm(prev => ({ ...prev, montant_ttc: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white font-bold focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Description / Notes</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 resize-none"
                  placeholder="Notes ou description..."
                />
              </div>

              {/* Conditions paiement */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Conditions de paiement</label>
                <input
                  type="text"
                  value={editForm.conditions_paiement}
                  onChange={(e) => setEditForm(prev => ({ ...prev, conditions_paiement: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="Ex: 30 jours fin de mois"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 px-6 py-4 border-t border-slate-700 bg-slate-800 flex gap-3">
              <button
                onClick={() => setEditingFacture(null)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={saveEdit}
                disabled={savingEdit}
                className="flex-1 py-3 bg-blue-500 hover:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {savingEdit ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {savingEdit ? 'Sauvegarde...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant Modal de Confirmation
function ConfirmationModal({ data, onConfirm, onCancel, formatMontant, formatDate }) {
  const [mods, setMods] = useState({});
  const extracted = { ...data.extracted_data, ...mods };
  const isAvoir = extracted.type_document?.toUpperCase() === 'AVOIR';
  const isAcompte = extracted.type_document?.toUpperCase() === "FACTURE D'ACOMPTE";

  const updateField = (field, value) => {
    setMods(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isAvoir ? 'ring-2 ring-pink-500/50' : isAcompte ? 'ring-2 ring-cyan-500/50' : 'ring-2 ring-amber-500/50'}`}>
        <div className={`sticky top-0 px-6 py-4 border-b flex items-center justify-between ${isAvoir ? 'bg-pink-500/10 border-pink-500/30' : isAcompte ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
          <div className="flex items-center gap-3">
            {isAvoir ? (
              <ArrowDownLeft className="w-6 h-6 text-pink-400" />
            ) : isAcompte ? (
              <CreditCard className="w-6 h-6 text-cyan-400" />
            ) : (
              <FileText className="w-6 h-6 text-amber-400" />
            )}
            <div>
              <h2 className={`text-xl font-bold ${isAvoir ? 'text-pink-400' : isAcompte ? 'text-cyan-400' : 'text-amber-400'}`}>
                Vérifier avant ajout
              </h2>
              <p className="text-slate-400 text-sm">Modifiez si nécessaire puis confirmez</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Type document */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Type de document</label>
              <select
                value={extracted.type_document || 'FACTURE'}
                onChange={(e) => updateField('type_document', e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border bg-slate-700 text-white focus:outline-none ${isAvoir ? 'border-pink-500/50 focus:border-pink-500' : isAcompte ? 'border-cyan-500/50 focus:border-cyan-500' : 'border-slate-600 focus:border-amber-500'}`}
              >
                <option value="FACTURE">FACTURE</option>
                <option value="FACTURE D'ACOMPTE">FACTURE D'ACOMPTE</option>
                <option value="AVOIR">AVOIR</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Fournisseur *</label>
              <input
                type="text"
                value={extracted.fournisseur || ''}
                onChange={(e) => updateField('fournisseur', e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">N° Facture/Avoir</label>
              <input
                type="text"
                value={extracted.numero_facture || ''}
                onChange={(e) => updateField('numero_facture', e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Réf. Commande</label>
              <input
                type="text"
                value={extracted.reference_commande || ''}
                onChange={(e) => updateField('reference_commande', e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Date de vente</label>
              <input
                type="text"
                value={extracted.date_commande || ''}
                onChange={(e) => updateField('date_commande', e.target.value)}
                placeholder="JJ/MM/AAAA"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-amber-400 focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Date facture</label>
              <input
                type="text"
                value={extracted.date_facture || ''}
                onChange={(e) => updateField('date_facture', e.target.value)}
                placeholder="JJ/MM/AAAA"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Date échéance</label>
              <input
                type="text"
                value={extracted.date_echeance || ''}
                onChange={(e) => updateField('date_echeance', e.target.value)}
                placeholder="JJ/MM/AAAA"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          {/* Montants */}
          <div className={`rounded-xl p-4 border ${isAvoir ? 'bg-pink-500/10 border-pink-500/30' : isAcompte ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
            <h3 className={`font-medium mb-3 ${isAvoir ? 'text-pink-400' : isAcompte ? 'text-cyan-400' : 'text-amber-400'}`}>Montants</h3>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">HT</label>
                <input
                  type="number"
                  step="0.01"
                  value={extracted.montant_ht || ''}
                  onChange={(e) => updateField('montant_ht', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">TVA %</label>
                <input
                  type="number"
                  step="0.1"
                  value={extracted.taux_tva || ''}
                  onChange={(e) => updateField('taux_tva', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">TVA €</label>
                <input
                  type="number"
                  step="0.01"
                  value={extracted.montant_tva || ''}
                  onChange={(e) => updateField('montant_tva', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">TTC</label>
                <input
                  type="number"
                  step="0.01"
                  value={extracted.montant_ttc || ''}
                  onChange={(e) => updateField('montant_ttc', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>
          </div>

          {/* Règlement */}
          <div className={`rounded-xl p-4 border ${extracted.est_reglee ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-700/50 border-slate-600'}`}>
            <h3 className="text-slate-300 font-medium mb-3">Règlement détecté</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Réglée ?</label>
                <select
                  value={extracted.est_reglee ? 'true' : 'false'}
                  onChange={(e) => updateField('est_reglee', e.target.value === 'true')}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-green-500"
                >
                  <option value="false">Non</option>
                  <option value="true">Oui</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Mode/Modalité</label>
                <input
                  type="text"
                  value={extracted.mode_paiement || ''}
                  onChange={(e) => updateField('mode_paiement', e.target.value)}
                  placeholder="Ex: REPRISE ACPTE, VIREMENT..."
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-slate-800 px-6 py-4 border-t border-slate-700 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm(mods)}
            className={`flex-1 py-3 text-white rounded-lg font-medium transition-colors ${isAvoir ? 'bg-pink-500 hover:bg-pink-400' : isAcompte ? 'bg-cyan-500 hover:bg-cyan-400' : 'bg-amber-500 hover:bg-amber-400'}`}
          >
            <Check className="w-4 h-4 inline mr-2" />
            Confirmer l'ajout
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant Modal de Règlement
function ReglementModal({ facture, avoirsDisponibles, onConfirm, onCancel, formatMontant }) {
  const [dateReglement, setDateReglement] = useState(new Date().toISOString().split('T')[0]);
  const [montantRegle, setMontantRegle] = useState(facture.montant_ttc || '');
  const [modePaiement, setModePaiement] = useState('');
  const [avoirId, setAvoirId] = useState('');

  const avoirSelectionne = avoirsDisponibles.find(a => a._id === avoirId);

  const handleSubmit = () => {
    if (!dateReglement || !montantRegle || !modePaiement) {
      alert("Veuillez remplir tous les champs");
      return;
    }
    onConfirm({
      date_reglement: dateReglement,
      montant_regle: parseFloat(montantRegle),
      mode_paiement: modePaiement,
      avoir_id: avoirId || null
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between bg-green-500/10">
          <div className="flex items-center gap-3">
            <Euro className="w-6 h-6 text-green-400" />
            <div>
              <h2 className="text-lg font-bold text-white">Régler la facture</h2>
              <p className="text-slate-400 text-sm">{facture.fournisseur} - {facture.numero_facture}</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Montant de la facture */}
          <div className="bg-slate-700/50 rounded-xl p-4 text-center">
            <p className="text-slate-400 text-sm">Montant TTC à régler</p>
            <p className="text-2xl font-bold text-white">{formatMontant(facture.montant_ttc)}</p>
          </div>

          {/* Date de règlement */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Date de règlement *</label>
            <input
              type="date"
              value={dateReglement}
              onChange={(e) => setDateReglement(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-green-500"
            />
          </div>

          {/* Montant réglé */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Montant réglé *</label>
            <input
              type="number"
              step="0.01"
              value={montantRegle}
              onChange={(e) => setMontantRegle(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-green-500"
            />
          </div>

          {/* Mode de paiement */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Mode de règlement *</label>
            <select
              value={modePaiement}
              onChange={(e) => setModePaiement(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-green-500"
            >
              <option value="">Sélectionner...</option>
              <option value="VIREMENT">Virement</option>
              <option value="CHEQUE">Chèque</option>
              <option value="CB">Carte bancaire</option>
              <option value="ESPECES">Espèces</option>
              <option value="PRELEVEMENT">Prélèvement</option>
              <option value="AVOIR">Compensation par avoir</option>
              <option value="REPRISE ACPTE">Reprise d'acompte</option>
              <option value="AUTRE">Autre</option>
            </select>
          </div>

          {/* Avoirs disponibles */}
          {avoirsDisponibles.length > 0 && (
            <div className="bg-pink-500/10 rounded-xl p-4 border border-pink-500/30">
              <label className="text-sm text-pink-400 mb-2 block font-medium">
                Utiliser un avoir disponible ?
              </label>
              <select
                value={avoirId}
                onChange={(e) => {
                  setAvoirId(e.target.value);
                  if (e.target.value) {
                    const avoir = avoirsDisponibles.find(a => a._id === e.target.value);
                    if (avoir) {
                      setModePaiement('AVOIR');
                    }
                  }
                }}
                className="w-full px-3 py-2.5 bg-slate-700 border border-pink-500/50 rounded-lg text-white focus:outline-none focus:border-pink-500"
              >
                <option value="">Ne pas utiliser d'avoir</option>
                {avoirsDisponibles.map(avoir => (
                  <option key={avoir._id} value={avoir._id}>
                    {avoir.fournisseur} - N°{avoir.numero_facture} - {formatMontant(avoir.montant_ttc)}
                  </option>
                ))}
              </select>
              {avoirSelectionne && (
                <p className="text-pink-300 text-sm mt-2">
                  Montant de l'avoir : {formatMontant(avoirSelectionne.montant_ttc)}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-700 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 py-3 bg-green-500 hover:bg-green-400 text-white rounded-lg font-medium transition-colors"
          >
            <Check className="w-4 h-4 inline mr-2" />
            Confirmer le règlement
          </button>
        </div>
      </div>
    </div>
  );
}
